import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageToolbar, EmptyState } from "@/components/PageToolbar";
import { Pencil, Trash2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import MobileInput, { isValidMobile } from "@/components/MobileInput";
import ImportButton from "@/components/ImportButton";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", phone: "", active: true });

  const load = async () => {
    const { data } = await api.get("/users");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { name: editing.name, email: editing.email, password: "", role: editing.role, phone: editing.phone || "", active: editing.active !== false }
        : { name: "", email: "", password: "", role: "staff", phone: "", active: true });
    }
  }, [open, editing]);

  const save = async (e) => {
    e.preventDefault();
    if (form.phone && !isValidMobile(form.phone)) {
      toast.error("Phone must be exactly 10 digits");
      return;
    }
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Staff updated");
      } else {
        await api.post("/users", payload);
        toast.success("Staff added");
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageToolbar
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="Add Staff"
        exportName={isAdmin ? "users" : null}
        extra={isAdmin ? (
          <ImportButton resource="users" onDone={load} label="Import Staff" />
        ) : undefined}
      />
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm crm-table">
          <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold w-10">Sr.</th>
              <th className="px-3 py-2.5 text-left font-semibold">Name</th>
              <th className="px-3 py-2.5 text-left font-semibold">Email</th>
              <th className="px-3 py-2.5 text-left font-semibold">Phone</th>
              <th className="px-3 py-2.5 text-left font-semibold">Role</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-3 py-2.5 text-right font-semibold w-40">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <tr key={r.id} data-testid="user-row">
                <td className="px-3 py-2 text-slate-500 font-mono-num">{i + 1}</td>
                <td className="px-3 py-2 font-semibold">{r.name}</td>
                <td className="px-3 py-2 text-slate-600 font-mono-num">{r.email}</td>
                <td className="px-3 py-2 text-slate-600 font-mono-num">{r.phone}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                    r.role === "admin"
                      ? "bg-[color:var(--brand-ochre)]/15 text-[color:var(--brand-ochre)]"
                      : r.role === "manager"
                      ? "bg-purple-100 text-purple-700"
                      : r.role === "field_executive"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-[color:var(--brand-teal)]/15 text-[color:var(--brand-teal)]"
                  }`}>{r.role === "field_executive" ? "Field Exec" : r.role}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                    r.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"
                  }`}>{r.active !== false ? "Active" : "Inactive"}</span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <button
                      data-testid="user-permissions-btn"
                      onClick={() => nav(`/role-permissions?user=${r.id}`)}
                      className="p-1.5 hover:bg-slate-100 rounded text-slate-600"
                      title="Manage permissions"
                    ><ShieldCheck className="h-4 w-4" /></button>
                    <button data-testid="edit-btn" onClick={() => { setEditing(r); setOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded text-[color:var(--brand-teal)]"><Pencil className="h-4 w-4" /></button>
                    <button data-testid="delete-btn" onClick={() => del(r)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <EmptyState label="No staff members yet" />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing ? "Edit Staff" : "Add Staff"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" data-testid="staff-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10" data-testid="staff-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Phone</Label>
              <MobileInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10" data-testid="staff-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Password {editing && <span className="text-slate-400 normal-case">(leave blank to keep)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} className="h-10" data-testid="staff-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-10" data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="field_executive">Field Executive</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Status</Label>
              <Select value={form.active ? "true" : "false"} onValueChange={(v) => setForm({ ...form, active: v === "true" })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="col-span-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white" data-testid="staff-save">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
